// @owners { team: patients }
import {
  type Attributes,
  type FeatureDefinition,
  type FeatureResult,
  GrowthBook,
  GrowthBookProvider,
  useFeatureValue,
} from '@growthbook/growthbook-react';
import React, { type PropsWithChildren, type ReactElement, useEffect } from 'react';
import { type AppFeatures } from './app-features';

let growthBookInstance: null | GrowthBook = null;
const isTest = !!process.env.JEST_WORKER_ID;
const initError = 'Experimentation.init has not been called yet';

type Interface = {
  /**
   * returns a provider that wraps the app and loads the feature flags. this is needed to use the Growthbook hooks
   * @example
   * const App = () => (
   *   <Experimentation.Provider>
   *     <App />
   *   </Experimentation.Provider>
   * )
   */
  Provider: ({ children }: PropsWithChildren) => ReactElement;
  /**
   * returns the list of features and their values
   * @example
   * const features = Experimentation.getFeatures()
   */
  getFeatures: () => Record<keyof AppFeatures, FeatureDefinition>;
  /**
   * initializes the experimentation library
   * @param clientKey the client key for the experimentation library
   * @param onFeatureUsage callback that is called when any feature flag is evaluated
   * @example
   * Experimentation.init('123', (featureKey, result) => {});
   */
  init: (clientKey: string, onFeatureUsage: (featureKey: string, result: FeatureResult) => void) => GrowthBook;
  /**
   * sets the attributes for the experimentation library
   * @param attributes the attributes to set
   * @example
   * Experimentation.setAttributes({ userId: '123' });
   */
  setAttributes: (attributes: Attributes) => void;
  /**
   * Gets the feature flag value of the feature key.
   * @param featureFlag the feature flag key
   * @param defaultValue the default value to return if Growthbook is not ready (optional)
   * @example
   * // Growthbook ready (server data)
   * const { value, isLoading } = Experimentation.useFeatureFlag('flagName', 2); // { value: 99, isLoading: false }
   * // Growthbook not ready (client default value)
   * const { value, isLoading } = Experimentation.useFeatureFlag('flagName', 2); // { value: 2, isLoading: true }
   */
  useFeatureFlag: <Key extends keyof AppFeatures>(
    featureFlag: Key,
    defaultValue?: AppFeatures[Key],
  ) => { isLoading: boolean; value: AppFeatures[Key] };
  /**
   * Reads a feature flag value synchronously (no hook). Use from class components or non-React code.
   * @param featureFlag the feature flag key
   * @param defaultValue the default value to return if Growthbook is not initialized
   * @example
   * const value = Experimentation.getFeatureValue('flagName', 2); // 99
   */
  getFeatureValue: <Key extends keyof AppFeatures>(
    featureFlag: Key,
    defaultValue: AppFeatures[Key],
  ) => AppFeatures[Key];
};

export const Experimentation: Interface = {
  init: (clientKey: string, onFeatureUsage: (featureKey: string, result: FeatureResult) => void) => {
    growthBookInstance = new GrowthBook<AppFeatures>({
      apiHost: 'https://growthbook-api.prod.alto.com',
      clientKey,
      enableDevMode: __DEV__,
      subscribeToChanges: true,
      // Feature reads run during render; defer so app callbacks (e.g. Redux dispatch) do not update subscribers mid-render.
      onFeatureUsage: (featureKey, result) => {
        queueMicrotask(() => {
          onFeatureUsage(featureKey, result);
        });
      },
    });
    return growthBookInstance;
  },
  setAttributes: (attributes: Attributes) => {
    if (!growthBookInstance) throw new Error(initError);
    growthBookInstance.setAttributes(attributes);
  },
  getFeatures: () => {
    if (!growthBookInstance) throw new Error(initError);
    return growthBookInstance.getFeatures();
  },
  Provider: ({ children }: PropsWithChildren) => {
    if (!growthBookInstance) throw new Error(initError);

    useEffect(() => {
      if (!growthBookInstance) throw new Error(initError);
      // Guard against re-fetching if a caller already awaited init() (e.g. to read a flag before this Provider mounts).
      if (!growthBookInstance.ready) {
        void growthBookInstance.init();
      }
    }, []);

    return <GrowthBookProvider growthbook={growthBookInstance}>{children}</GrowthBookProvider>;
  },
  useFeatureFlag: <Key extends keyof AppFeatures>(featureFlag: Key, defaultValue?: AppFeatures[Key]) => {
    if (!growthBookInstance && !isTest) throw new Error(initError);
    const value = (useFeatureValue(featureFlag, defaultValue ?? null) as AppFeatures[Key]) ?? false;
    return { value, isLoading: !growthBookInstance?.ready };
  },
  getFeatureValue: <Key extends keyof AppFeatures>(featureFlag: Key, defaultValue: AppFeatures[Key]) => {
    if (!growthBookInstance) {
      // This one is to track the calls before init, so we fall back to defaultValue instead of throwing error
      if (__DEV__ && !isTest) {
        console.warn(`Experimentation.getFeatureValue('${featureFlag}') called before Experimentation.init()`);
      }
      return defaultValue;
    }
    return growthBookInstance.getFeatureValue(featureFlag, defaultValue) as AppFeatures[Key];
  },
};
